import { useEffect, useMemo } from 'react';
import { shuffle } from 'lodash';
import { ALMA_CATEGORY } from '~/components/AccountGetsMarried/constants/alma';
import {
  consumerServiceCategories,
  ConsumerServiceCategory,
} from '~/components/AccountGetsMarried/constants/ConsumerServices/consumerServiceCategories';
import { analyticsTrackEvent } from '~/core/analytics';
import { ALMA_CARD_VISIBLE, MEDMO_CARD_VISIBLE } from '~/core/analytics/events';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import useSelectActiveUserProfile from '~/hooks/useSelectActiveUserProfile';
import { isUserProfileAMinor } from '~/core/util/userProfile';
import { MEDMO_CATEGORY } from '~/components/AccountGetsMarried/constants/medmo';
import { useCurrentCareRegion } from '~/hooks/useCurrentCareRegion';
import { getAgeFromBirthDate } from '~/core/util/date';
import useShowLabs from './useShowLabs';

// Store this in module memory so it's consistent across re-mounts and renders for a user's session.
// Reloads will still change order.
let sortedOrder: number[] | null = null;

/**
 * A hook that returns a sorted list of Consumer Service categories, that also hides `hidden`
 * categories and any that are controlled by an ongoing A/B test.
 *
 * @param entrypoint -- An optional entrypoint to be passed into tracking events.
 */
export function useConsumerServiceCategories(entrypoint?: string) {
  const { snailsAlmaEnabled, snailsMedmoEnabledRegions } = useLDFlags();

  const profile = useSelectActiveUserProfile();
  const careRegion = useCurrentCareRegion();

  const showLabs = useShowLabs();

  let medmoRegions = snailsMedmoEnabledRegions as string[];
  let currentRegion = useCurrentCareRegion();

  const inEnabledMemoRegion = useMemo(() => {
    if (!currentRegion) return false;
    return medmoRegions.map((r) => r.toLowerCase()).includes(currentRegion?.toLowerCase());
  }, [currentRegion, medmoRegions]);

  useEffect(() => {
    analyticsTrackEvent(ALMA_CARD_VISIBLE, { visible: snailsAlmaEnabled, entrypoint });
  }, [entrypoint, snailsAlmaEnabled]);

  useEffect(() => {
    analyticsTrackEvent(MEDMO_CARD_VISIBLE, { visible: inEnabledMemoRegion, entrypoint });
  }, [entrypoint, inEnabledMemoRegion]);

  const userProfile = useSelectActiveUserProfile();
  const isMinor = isUserProfileAMinor(userProfile);

  const categories = useMemo(() => {
    const filtered = consumerServiceCategories
      .slice(0)
      .map((cat) => {
        let category = { ...cat };

        if (isMinor) {
          return {
            ...category,
            sortOrder: category.kids.sortOrder ?? category.sortOrder,
            examples: category.kids.examples ?? category.examples,
            description: category.kids.description ?? category.description,
            hidden: category.kids.hidden ?? category.hidden,
          };
        }
        return category;
      })
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .filter((category) => !category.hidden)
      .filter((category) => {
        if (category.slug === ALMA_CATEGORY) {
          if (!snailsAlmaEnabled) {
            return false;
          }
        }
        if (!inEnabledMemoRegion && category.slug === MEDMO_CATEGORY) {
          return false;
        }

        if (category.slug === 'labs') {
          return showLabs;
        }
        return true;
      });

    // Shuffle the categories so that they don't always appear in the same order, and we can get more
    // accurate CTR data.
    if (!isMinor) {
      if (!sortedOrder) {
        let shuffled = shuffle(filtered);
        sortedOrder = shuffled.map((item) => item.sortOrder);
      }

      // re-sort "New" categories to the top
      const reSorted = filtered.sort((a, b) => {
        if (a.new && !b.new) return -1;
        if (!a.new && b.new) return 1;
        return a.sortOrder - b.sortOrder;
      });

      return reSorted;
    }
    return filtered;
  }, [inEnabledMemoRegion, isMinor, showLabs, snailsAlmaEnabled]);

  return categories;
}
